// Option definitions for the EMS Patient Care Report (PCR) form.
// Whole-year age from a YYYY-MM-DD date of birth ("" if unknown/invalid).
export function ageFromDob(dob) {
    if (!dob)
        return "";
    const d = new Date(dob);
    if (isNaN(d.getTime()))
        return "";
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate()))
        age--;
    return age >= 0 && age < 150 ? String(age) : "";
}
export const TIMESTAMP_FIELDS = [
    ["dispatch", "Dispatch"],
    ["enRoute", "En Route"],
    ["onScene", "On Scene / At Facility"],
    ["patientContact", "Patient Contact"],
    ["transport", "Transport"],
    ["atDestination", "At Destination"],
    ["backToBase", "Back to Base"],
    ["backInService", "Back In Service"],
];
export const GENDERS = ["Male", "Female"];
export const ID_TYPES = ["National ID", "Iqama", "Passport"];
export const TRIAGE_LEVELS = [
    "I - Resuscitation",
    "II - Emergent",
    "III - Urgent",
    "IV - Less Urgent",
    "V - Non Urgent",
];
export const MEDICAL_HISTORY = [
    ["none", "NONE"],
    ["unknown", "Unknown"],
    ["cardiac", "Cardiac"],
    ["stroke", "Stroke / CVA"],
    ["seizure", "Seizure"],
    ["diabetes", "Diabetes"],
    ["chf", "CHF"],
    ["htn", "HTN"],
    ["asthma", "Asthma"],
    ["renalFailure", "Renal Failure"],
];
// Patient assessment — one selection per category.
export const ASSESSMENT = [
    {
        key: "levelOfConsciousness",
        label: "Level of Consciousness",
        options: ["Alert", "Respond to voice", "Respond to pain", "Unresponsive"],
    },
    {
        key: "airway",
        label: "Airway",
        options: ["Patent", "Partially Obstructed", "Obstructed"],
    },
    {
        key: "breathing",
        label: "Breathing",
        options: ["Normal", "Tachypnea", "Bradypnea", "Apnea"],
    },
    {
        key: "pulse",
        label: "Pulse",
        options: ["Strong", "Weak", "Regular", "Irregular"],
    },
    {
        key: "skinCondition",
        label: "Skin Condition",
        options: ["Normal", "Pale", "Cyanotic", "Flushed"],
    },
    {
        key: "skinTemperature",
        label: "Skin Temperature",
        options: ["Warm", "Cold", "Warm to touch"],
    },
    {
        key: "pupils",
        label: "Pupils Reaction",
        options: ["Equal", "Dilated", "Constricted", "Equal & Reactive"],
    },
];
export const GCS_EYE = [
    ["4", "Spontaneously"],
    ["3", "To speech"],
    ["2", "To pain"],
    ["1", "No response"],
];
export const GCS_VERBAL = [
    ["5", "Oriented to time, place and person"],
    ["4", "Confused"],
    ["3", "Inappropriate words"],
    ["2", "Incomprehensible sounds"],
    ["1", "No response"],
];
export const GCS_MOTOR = [
    ["6", "Obeys command"],
    ["5", "Moves to localised pain"],
    ["4", "Flex to withdraw from pain"],
    ["3", "Abnormal flexion"],
    ["2", "Abnormal extension"],
    ["1", "No response"],
];
export const VITAL_COLUMNS = [
    ["time", "Time"],
    ["pulse", "Pulse"],
    ["bp", "BP mmHg"],
    ["rr", "RR"],
    ["rbs", "RBS"],
    ["spo2", "SpO₂"],
    ["temp", "Temp °C"],
    ["pain", "Pain /10"],
];
export const CARDIAC_RHYTHMS = ["PEA", "VT", "VF", "Asystole"];
export const BODY_REGIONS = [
    "Head",
    "Ears",
    "Face",
    "Neck",
    "Adam's Apple",
    "Shoulder",
    "Arms",
    "Hands",
    "Chest",
    "Abdomen",
    "Pelvis",
    "Genitalia",
    "Legs",
    "Foot",
    "Back",
];
export const DCAP_BTLS = "D=Deformity · C=Contusions · A=Abrasions · P=Punctures/Penetration · B=Burns · T=Tenderness · L=Lacerations · S=Swelling";
// Dropdown options for each body region in the physical exam (one finding).
export const DCAP_OPTIONS = [
    "D — Deformity",
    "C — Contusions",
    "A — Abrasions",
    "P — Punctures/Penetration",
    "B — Burns",
    "T — Tenderness",
    "L — Lacerations",
    "S — Swelling",
];
export const INTERVENTION_AIRWAY = [
    ["suction", "Suction"],
    ["opa", "OPA"],
    ["npa", "NPA"],
    ["ett", "ETT"],
    ["lma", "LMA"],
];
export const INTERVENTION_BREATHING = [
    ["nasalMask", "Nasal mask"],
    ["simpleFaceMask", "Simple face mask"],
    ["nonRebreather", "Non rebreather mask"],
    ["nebulizer", "Nebulizer mask"],
    ["bvm", "BVM"],
];
export const INTERVENTION_TRAUMA = [
    ["cCollar", "C-Collar"],
    ["backBoard", "Back board"],
    ["scopeBoard", "Scope board"],
    ["ked", "KED"],
    ["bleedingControl", "Bleeding control"],
    ["splint", "Splint"],
];
export const MED_COLUMNS = [
    ["time", "Time"],
    ["medication", "Medication"],
    ["dose", "Dose"],
    ["route", "Route"],
    ["sign", "Sign"],
];
export const TEAM_TYPES = [
    "Paramedic Unit",
    "Full team",
    "Doctors",
    "Nursing",
    "Respiratory Therapy",
];
export const REFUSAL_TEXT_EN = "I, the undersigned, being of sound mind, hereby acknowledge that my medical condition has been clearly explained to me by the attending paramedic. I fully understand the nature of my condition and the potential risks and complications that may result from refusing transport or further medical care. Despite this explanation, I voluntarily and knowingly refuse transport or further treatment and take full responsibility for any consequences arising from my decision.";
export const REFUSAL_TEXT_AR = "أنا الموقع أدناه، وبكامل قواي العقلية، أقرّ بأنه قد تم توضيح حالتي الطبية لي من قبل المسعف المناوب، وأفهم تمامًا طبيعة حالتي والمخاطر أو المضاعفات المحتملة الناتجة عن رفضي للنقل أو تلقي الرعاية الطبية، ورغم هذا الإيضاح، فإنني أرفض طوعًا وعن دراية تامة النقل أو أي علاج إضافي، وأتحمل كامل المسؤولية عن أي تبعات أو نتائج تترتب على قراري هذا.";
//# sourceMappingURL=pcrFields.js.map